import { useEffect, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

export interface MobileDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Callback when the drawer should close */
  onClose: () => void;
  /** Title displayed in the header */
  title?: string;
  /** Header content (replaces title if provided) */
  header?: ReactNode;
  /** Content of the drawer */
  children: ReactNode;
  /** Position of the drawer (default: 'right') */
  position?: 'left' | 'right';
  /** Width of the drawer (default: 'w-80 max-w-[85vw]') */
  width?: string;
  /** Background class for the drawer (default: 'bg-white') */
  bgClassName?: string;
  /** Header background class */
  headerClassName?: string;
  /** Additional class name for the drawer */
  className?: string;
  /** Optional footer content rendered below the scrollable content area */
  footer?: ReactNode;
}

/**
 * Mobile-friendly slide-out drawer component.
 * Slides from left or right edge of the screen with backdrop.
 *
 * @example
 * ```tsx
 * <MobileDrawer
 *   isOpen={isMenuOpen}
 *   onClose={() => setIsMenuOpen(false)}
 *   title="Menu"
 *   position="right"
 *   headerClassName="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
 * >
 *   <nav className="space-y-2">
 *     {navItems.map(item => (
 *       <NavLink key={item.to} to={item.to} onClick={onClose}>
 *         {item.label}
 *       </NavLink>
 *     ))}
 *   </nav>
 * </MobileDrawer>
 * ```
 */
export function MobileDrawer({
  isOpen,
  onClose,
  title,
  header,
  children,
  position = 'right',
  width = 'w-80 max-w-[85vw]',
  bgClassName = 'bg-white',
  headerClassName,
  className,
  footer,
}: MobileDrawerProps) {
  // Keep drawer mounted during close animation
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- drives mount/visibility transition on open
      setMounted(true);
      // Trigger animation on next frame so the initial off-screen position is painted first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mounted]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const isRight = position === 'right';

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
      />

      {/* Slide-out drawer */}
      <div
        style={{ transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
        className={clsx(
          'fixed top-0 z-50 h-full shadow-2xl will-change-transform',
          width,
          bgClassName,
          'flex flex-col',
          'transform transition-transform duration-[350ms]',
          isRight ? 'right-0' : 'left-0',
          visible ? 'translate-x-0' : isRight ? 'translate-x-full' : '-translate-x-full',
          className
        )}
      >
        {/* Header */}
        {(title || header) && (
          <div className={clsx('border-b p-4', headerClassName)}>
            {header || (
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-balance">{title}</h2>
                <button
                  onClick={onClose}
                  className="rounded-lg p-2 transition-colors hover:bg-white/20"
                  aria-label="סגור תפריט"
                >
                  <X size={24} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Footer */}
        {footer && <div className="flex-shrink-0">{footer}</div>}
      </div>
    </>
  );
}
